import React, { useEffect, useState } from 'react';

/**
 * SplashOverlay - Full-screen brand intro overlay
 * Renders on top of the app and fades out after loading
 * Shows once per session for all users (logged in or not)
 */
const SplashOverlay = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Animate progress bar over 2.5 seconds
    const duration = 2500;
    const interval = 50;
    const steps = duration / interval;
    const increment = 100 / steps;
    
    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        // Start fade out
        setFadeOut(true);
        // Complete after fade animation
        setTimeout(() => {
          onComplete();
        }, 400);
      }
      setProgress(currentProgress);
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-between overflow-hidden transition-opacity duration-400 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: '#0B101B' }}
      data-testid="splash-overlay"
    >
      {/* Background Decorative Glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(6, 182, 212, 0.08) 0%, transparent 70%)'
        }}
      />
      
      {/* Subtle Pulse Ring */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5">
        <div 
          className="w-[120%] h-[120%] rounded-full border border-cyan-500 animate-pulse"
          style={{ animationDuration: '3s' }}
        />
      </div>

      {/* Top Spacer */}
      <div className="mt-20" />

      {/* Center Content - Logo & Taglines */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo */}
        <div className="flex items-center mb-6">
          <h1 className="text-6xl md:text-7xl font-black tracking-tight text-white">
            SPORT
          </h1>
          <h1 
            className="text-6xl md:text-7xl font-black tracking-tight text-cyan-400 animate-pulse"
            style={{ 
              textShadow: '0 0 20px rgba(6, 182, 212, 0.6), 0 0 40px rgba(6, 182, 212, 0.3)',
              animationDuration: '3s'
            }}
          >
            X
          </h1>
        </div>

        {/* Primary Tagline */}
        <p className="text-sm md:text-base font-bold tracking-[0.25em] text-white uppercase mb-4">
          No Gambling. All Game.
        </p>

        {/* Subtitle */}
        <p className="text-white/60 text-sm md:text-base text-center max-w-xs">
          Real-time social auctions for sports fans
        </p>
      </div>

      {/* Bottom Section - Badge & Loading */}
      <div className="relative z-10 w-full max-w-xs flex flex-col items-center mb-16 px-6">
        {/* Security Badge */}
        <div className="flex items-center gap-2 text-white/40 mb-8">
          <span className="material-symbols-outlined text-cyan-500 text-sm">verified_user</span>
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold">
            Secure Competition Environment
          </span>
        </div>

        {/* Loading Bar Container */}
        <div className="w-full h-[2px] bg-white/10 rounded-full overflow-hidden">
          {/* Progress Fill */}
          <div 
            className="h-full bg-cyan-500 rounded-full transition-all duration-100 ease-out"
            style={{ 
              width: `${progress}%`,
              boxShadow: '0 0 10px rgba(6, 182, 212, 0.8)'
            }}
          />
        </div>

        {/* iOS Home Indicator Style */}
        <div className="mt-8 w-32 h-1 bg-white/20 rounded-full" />
      </div>
    </div>
  );
};

export default SplashOverlay;
